import React, { useRef, useEffect, useState } from 'react';
import { ExternalLink, Globe, Smartphone } from 'lucide-react';

interface ProjectColorScheme {
  bg: string;
  border: string;
  accent: string;
  textColor: string;
  textMuted: string;
  bgNeutral: string;
  borderNeutral: string;
  accentNeutral: string;
  textColorNeutral: string;
  textMutedNeutral: string;
}

interface ProjectData {
  title: string;
  description: string;
  tags: string[];
  icon: React.ReactNode;
  featured?: boolean;
  demoUrl?: string;
  colorScheme: ProjectColorScheme;
}

// Extracted sub-component — hooks are at the top level (no rules violation)
const PortfolioCard: React.FC<{
  project: ProjectData;
  cardIndex: number;
  isActive: boolean;
  cardRef: (el: HTMLDivElement | null) => void;
  onClick?: () => void;
}> = ({ project, cardIndex, isActive: activeFromParent, cardRef, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isActive = isHovered || activeFromParent;
  const colors = isActive ? project.colorScheme : {
    bg: project.colorScheme.bgNeutral,
    border: project.colorScheme.borderNeutral,
    accent: project.colorScheme.accentNeutral,
    textColor: project.colorScheme.textColorNeutral,
    textMuted: project.colorScheme.textMutedNeutral
  };

  return (
    <div
      ref={cardRef}
      data-card-index={cardIndex}
      className={`portfolio-card border-2 p-6 md:p-8 transition-all duration-500 hover:scale-[1.02] hover:shadow-lg animate-on-scroll group cursor-pointer ${
        project.featured ? 'lg:col-span-2' : ''
      }`}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      data-delay={`${cardIndex * 0.1}s`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 border rounded-lg transition-all duration-500" style={{
            borderColor: `${colors.accent}40`,
            backgroundColor: `${colors.accent}15`
          }}>
            <div className="transition-all duration-500" style={{
              color: colors.accent
            }}>
              {project.icon}
            </div>
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold mb-2 transition-all duration-500" style={{
              color: colors.textColor
            }}>
              {project.title}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-1 transition-all duration-500 whitespace-nowrap" style={{
          color: project.demoUrl 
            ? (isActive ? colors.accent : '#1a1a1a') 
            : `${colors.textMuted}60`
        }}>
          <span className="text-xs font-mono uppercase tracking-wider">
            {project.demoUrl ? 'Visit Website' : 'Link Coming Soon'}
          </span>
          <ExternalLink 
            size={14} 
            strokeWidth={1.5}
          />
        </div>
      </div>
      
      <p className="mb-6 leading-relaxed text-base transition-all duration-500" style={{
        color: colors.textMuted
      }}>
        {project.description}
      </p>
      
      <div className="pt-6 transition-all duration-500" style={{
        borderTop: `1px solid ${colors.border}60`
      }}>
        <p className="font-mono text-xs uppercase tracking-widest mb-4 transition-all duration-500" style={{
          color: `${colors.textMuted}90`
        }}>TECHNOLOGIES:</p>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, tagIndex) => (
            <span 
              key={tagIndex}
              className="px-3 py-1 text-sm border rounded-full font-mono transition-all duration-500"
              style={{
                borderColor: `${colors.accent}30`,
                color: colors.textMuted,
                backgroundColor: `${colors.accent}10`
              }}
            >
              › {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const CaseStudiesSection: React.FC = () => {
  const [activeCards, setActiveCards] = useState<Set<number>>(new Set());
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Check if it's a touch device
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  
  useEffect(() => {
    if (!isTouchDevice) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute('data-card-index') || '0');
          
          setActiveCards(prev => {
            const newSet = new Set(prev);
            if (entry.isIntersecting) {
              newSet.add(index);
            } else {
              newSet.delete(index);
            }
            return newSet;
          });
        });
      },
      {
        threshold: [0.3, 0.7],
        rootMargin: '-15% 0px -15% 0px'
      }
    );
    
    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    
    return () => {
      cardRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [isTouchDevice]);
  
  const portfolioProjects: ProjectData[] = [
    {
      title: 'SentelBox',
      description: 'Remote CCTV Access Gateway without Static IP. Industrial-grade, plug-and-play gateway with end-to-end encryption for NVR streaming, custom customer controls, and administrative device monitoring.',
      tags: ['PWA Web App', 'Edge Computing', 'Hardware Gateway', 'Admin & Customer Portal'],
      icon: <Smartphone size={24} strokeWidth={1.5} />,
      featured: true,
      demoUrl: 'https://sentelbox.paokhum.com/',
      colorScheme: {
        bg: '#0A0F1D', border: '#0EA5E9', accent: '#38BDF8', textColor: '#F8FAFC', textMuted: '#94A3B8',
        bgNeutral: '#F5F5F5', borderNeutral: '#E0E0E0', accentNeutral: '#757575', textColorNeutral: '#424242', textMutedNeutral: '#616161'
      }
    },
    {
      title: 'HEYVUN Coins',
      description: 'Instant game coin top-up platform built as a PWA. Features complete end-to-end UX/UI design and custom development for both customer storefront and real-time admin management portal.',
      tags: ['PWA Web App', 'Admin & Customer Portal', 'UX/UI', 'E-commerce'],
      icon: <Globe size={24} strokeWidth={1.5} />,
      demoUrl: 'https://coins.heyvun.in/',
      colorScheme: {
        bg: '#0F111A', border: '#8B5CF6', accent: '#F59E0B', textColor: '#FFFFFF', textMuted: '#D1D5DB',
        bgNeutral: '#F5F5F5', borderNeutral: '#E0E0E0', accentNeutral: '#757575', textColorNeutral: '#424242', textMutedNeutral: '#616161'
      }
    },
    {
      title: 'Haangbro',
      description: 'Real-time local discovery & store status platform. A progressive web app allowing users to explore nearby cafes, bakeries, and hidden gems with live open/closed tracking, navigation, and dual customer & merchant portals.',
      tags: ['PWA Web App', 'Admin & Customer Portal', 'UX/UI', 'Responsive Website'],
      icon: <Globe size={24} strokeWidth={1.5} />,
      demoUrl: 'https://www.haangbro.com',
      colorScheme: {
        bg: '#0B1315', border: '#00E5FF', accent: '#00F2FE', textColor: '#FFFFFF', textMuted: '#94A3B8',
        bgNeutral: '#F5F5F5', borderNeutral: '#E0E0E0', accentNeutral: '#757575', textColorNeutral: '#424242', textMutedNeutral: '#616161'
      }
    },
    {
      title: 'Mikup',
      description: 'Frictionless event photo & video sharing platform. A single QR code lets guests collect and share full-size 4K photos instantly, with no app downloads or sign-ups required.',
      tags: ['UX/UI', 'Branding', 'Responsive Website'],
      icon: <Smartphone size={24} strokeWidth={1.5} />,
      demoUrl: 'https://mikup.pages.dev/',
      colorScheme: {
        bg: '#1C1917', border: '#F97316', accent: '#FB923C', textColor: '#FAFAF9', textMuted: '#D6D3D1',
        bgNeutral: '#F5F5F5', borderNeutral: '#E0E0E0', accentNeutral: '#757575', textColorNeutral: '#424242', textMutedNeutral: '#616161'
      }
    }
  ];

  const paokhum: ProjectData[] = [];

  return (
    <section id="portfolio" className="relative py-20 md:py-24 section-backdrop-light">
      <div className="section-grain" aria-hidden="true" />
      <div className="container relative mx-auto px-3 sm:px-4 lg:px-6">
        <div className="mb-16 relative z-10">
          <div className="flex items-center lg:hidden mb-3">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-2">[ 02 ]</span>
            <div className="w-20 border-t-2 border-foreground" />
          </div>
          <div className="hidden lg:flex flex-row items-baseline">
            <span className="font-mono text-xs tracking-widest text-foreground/60 mr-6">[ 02 ]</span>
            <div>
              <div className="w-20 border-t-2 border-foreground mb-4"></div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Work in Action</h2>
            </div>
          </div>
          <div className="lg:hidden">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight">Our Work in Action</h2>
          </div>
        </div>

        {/* Portfolio Grid */}
        <div className="space-y-12 max-w-6xl ml-auto relative z-10">
          {/* Main Projects */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {portfolioProjects.map((project, index) => (
              <PortfolioCard
                key={index}
                project={project}
                cardIndex={index}
                isActive={activeCards.has(index)}
                cardRef={el => cardRefs.current[index] = el}
                onClick={project.demoUrl ? () => window.open(project.demoUrl, '_blank', 'noopener,noreferrer') : undefined}
              />
            ))}
          </div>

          {/* Paokhum Projects */}
          {paokhum.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {paokhum.map((project, index) => {
                const cardIndex = portfolioProjects.length + index;
                return (
                  <PortfolioCard
                    key={index}
                    project={project}
                    cardIndex={cardIndex}
                    isActive={activeCards.has(cardIndex)}
                    cardRef={el => cardRefs.current[cardIndex] = el}
                    onClick={project.demoUrl ? () => window.open(project.demoUrl, '_blank', 'noopener,noreferrer') : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* More coming soon */}
        <div className="text-center mt-16 animate-on-scroll" data-delay="0.2s">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-mono uppercase tracking-widest bg-foreground/5 text-foreground/70 border border-foreground/10">
            More case studies coming soon
          </span>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
